import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

type SyntaxStyle = Record<string, Record<string, string | number>>;

export const codeFontFamily = '"Cascadia Code", Consolas, "Courier New", monospace';

/**
 * A small Prism theme built from the active MUI palette. Keeping this separate
 * prevents code surfaces from being tied to a particular dark theme.
 */
export const getCodeSyntaxTheme = (theme: Theme): SyntaxStyle => {
	const { background, primary, success, text, warning } = theme.palette;
	const accent = primary.light ?? primary.main;
	const muted = alpha(text.secondary, 0.78);

	return {
		'pre[class*="language-"]': { color: text.primary, background: background.default, textShadow: 'none' },
		'code[class*="language-"]': { color: text.primary, textShadow: 'none' },
		'pre[class*="language-"]::selection': { background: alpha(primary.main, 0.35), textShadow: 'none' },
		'code[class*="language-"]::selection': { background: alpha(primary.main, 0.35), textShadow: 'none' },
		'pre[class*="language-"] *::selection': { background: alpha(primary.main, 0.35), textShadow: 'none' },
		'code[class*="language-"] *::selection': { background: alpha(primary.main, 0.35), textShadow: 'none' },
		comment: { color: muted, fontStyle: 'italic' },
		prolog: { color: muted },
		punctuation: { color: text.secondary },
		keyword: { color: primary.main },
		'keyword.module': { color: primary.main },
		'keyword.control-flow': { color: primary.light ?? primary.main },
		function: { color: accent },
		'function.maybe-class-name': { color: accent },
		'class-name': { color: success.main },
		'maybe-class-name': { color: success.main },
		string: { color: warning.light ?? warning.main },
		char: { color: warning.light ?? warning.main },
		builtin: { color: warning.light ?? warning.main },
		number: { color: success.light ?? success.main },
		boolean: { color: primary.light ?? primary.main },
		constant: { color: accent },
		property: { color: accent },
		variable: { color: accent },
		parameter: { color: accent },
		operator: { color: text.primary },
		'attr-name': { color: accent },
		'attr-value': { color: warning.light ?? warning.main },
	};
};
